import { Link } from "react-router-dom";
import { useState } from "react";
import { Pencil, Eye, LayoutDashboard, LogOut, ChevronUp } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useEditMode } from "../contexts/EditModeContext";

/**
 * Floating CMS bar — only renders for authenticated admins on PUBLIC routes.
 * Provides the WordPress-style "Edit Mode" toggle and quick links back to admin.
 */
export default function EditModeBar() {
    const { user, logout } = useAuth();
    const { editMode, toggle } = useEditMode();
    const [collapsed, setCollapsed] = useState(false);

    if (!user) return null;

    return (
        <div
            data-testid="cms-bar"
            className="fixed bottom-6 left-6 z-50 select-none"
        >
            {collapsed ? (
                <button
                    onClick={() => setCollapsed(false)}
                    className="h-12 w-12 rounded-full bg-aimploy-navy text-white flex items-center justify-center shadow-[0_12px_30px_rgba(26,26,46,0.35)] hover:bg-aimploy-navy-hover"
                    aria-label="CMS-Bar öffnen"
                >
                    <Pencil className="w-5 h-5" />
                </button>
            ) : (
                <div className="bg-aimploy-navy text-white rounded-2xl shadow-[0_20px_50px_rgba(26,26,46,0.35)] border border-white/10 overflow-hidden backdrop-blur-md">
                    <div className="flex items-center gap-1 p-2">
                        <button
                            onClick={toggle}
                            data-testid="cms-edit-toggle"
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                                editMode
                                    ? "bg-aimploy-violet text-white"
                                    : "bg-white/5 text-white/80 hover:bg-white/10"
                            }`}
                            title={editMode ? "Vorschau-Modus" : "Bearbeitungs-Modus"}
                        >
                            {editMode ? <Eye className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                            {editMode ? "Vorschau" : "Bearbeiten"}
                        </button>
                        <Link
                            to="/admin"
                            data-testid="cms-admin-link"
                            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                            title="Zum Dashboard"
                        >
                            <LayoutDashboard className="w-4 h-4" />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Link>
                        <button
                            onClick={logout}
                            data-testid="cms-logout"
                            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                            title="Abmelden"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setCollapsed(true)}
                            className="flex items-center px-2 py-2.5 rounded-xl text-white/40 hover:text-white"
                            title="Einklappen"
                            aria-label="Einklappen"
                        >
                            <ChevronUp className="w-4 h-4 rotate-180" />
                        </button>
                    </div>
                    {editMode && (
                        <div className="px-4 pb-3 -mt-1">
                            <div className="flex items-center gap-2 text-[11px] text-aimploy-violet/90">
                                <span className="h-1.5 w-1.5 rounded-full bg-aimploy-violet animate-pulse" />
                                Klicken Sie auf einen Text, um ihn zu bearbeiten. Beim Verlassen wird automatisch gespeichert.
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
